import { motion } from "framer-motion";

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (delay: number) => ({
    pathLength: 1,
    opacity: 1,
    transition: { pathLength: { delay, duration: 1.5, ease: "easeInOut" as const }, opacity: { delay, duration: 0.3 } },
  }),
};

// Calendar/grid icon
export const DoodleCalendar = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="44" height="44" viewBox="0 0 44 44" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.rect x="4" y="8" width="36" height="32" rx="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.2} />
    <motion.path d="M4 18H40M14 4V12M30 4V12M14 24H18M26 24H30M14 32H18M26 32H30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.8} />
  </motion.svg>
);

// Clock / time icon
export const DoodleClock = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="40" height="40" viewBox="0 0 40 40" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.3} />
    <motion.path d="M20 10V20L27 27" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={1.0} />
  </motion.svg>
);

// Cursor / click pointer
export const DoodleCursor = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="36" height="44" viewBox="0 0 36 44" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.path d="M6 4L6 32L14 24L22 38L28 35L20 21L30 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={0.3} />
  </motion.svg>
);

// Code brackets </>
export const DoodleCode = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="50" height="36" viewBox="0 0 50 36" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.path d="M15 4L4 18L15 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={0.2} />
    <motion.path d="M35 4L46 18L35 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={0.5} />
    <motion.path d="M30 2L20 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.8} />
  </motion.svg>
);

// Wi-Fi / connection signal
export const DoodleSignal = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="44" height="36" viewBox="0 0 44 36" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.path d="M22 32C22 32 22 30 22 30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" variants={draw} custom={0.2} />
    <motion.path d="M14 26C17 23 20 22 22 22C24 22 27 23 30 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.5} />
    <motion.path d="M7 20C12 15 17 13 22 13C27 13 32 15 37 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.8} />
    <motion.path d="M2 14C8 7 14 4 22 4C30 4 36 7 42 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={1.1} />
  </motion.svg>
);

// Gear / settings cog
export const DoodleGear = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="44" height="44" viewBox="0 0 44 44" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.circle cx="22" cy="22" r="7" stroke="currentColor" strokeWidth="2" variants={draw} custom={0.3} />
    <motion.path d="M22 4V10M22 34V40M4 22H10M34 22H40M9 9L13.5 13.5M30.5 30.5L35 35M35 9L30.5 13.5M13.5 30.5L9 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.8} />
  </motion.svg>
);

// Lightning bolt
export const DoodleBolt = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="30" height="44" viewBox="0 0 30 44" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.path d="M18 2L4 24H15L12 42L26 18H16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={0.3} />
  </motion.svg>
);

// Database / stack
export const DoodleDatabase = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="40" height="44" viewBox="0 0 40 44" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.ellipse cx="20" cy="10" rx="16" ry="6" stroke="currentColor" strokeWidth="2" variants={draw} custom={0.2} />
    <motion.path d="M4 10V34C4 37.3 11.2 40 20 40C28.8 40 36 37.3 36 34V10" stroke="currentColor" strokeWidth="2" variants={draw} custom={0.6} />
    <motion.path d="M4 22C4 25.3 11.2 28 20 28C28.8 28 36 25.3 36 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={1.0} />
  </motion.svg>
);

export const DoodleArrowCurved = ({ className = "" }: { className?: string }) => (
  <motion.svg className={className} width="120" height="60" viewBox="0 0 120 60" fill="none" initial="hidden" whileInView="visible" viewport={{ once: true }}>
    <motion.path d="M5 50C20 50 40 10 60 10C80 10 100 50 115 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" variants={draw} custom={0.3} />
    <motion.path d="M105 25L115 30L108 38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={draw} custom={1.5} />
  </motion.svg>
);

export const FloatingDoodle = ({
  children,
  delay = 0,
  duration = 4,
  y = 8,
}: {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  y?: number;
}) => (
  <motion.div
    animate={{ y: [0, -y, 0] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
  >
    {children}
  </motion.div>
);
